import {
  Add as AddIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  LocalShipping as LocalShippingIcon,
  NoteAlt as NoteIcon,
  OpenInNew as OpenInNewIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Phone as PhoneIcon,
  PostAdd as PostAddIcon,
  Search as SearchIcon,
  Sms as SmsIcon,
  AssignmentTurnedIn as TaskIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Theme,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab'
import type { Vehicle } from '@otw/models'
import type {
  Contact,
  ContactActivityType,
  ContactStage,
  CrmActivityDomain,
} from '@otw/models/leads/contacts'
import { getContactStageAt } from '@otw/models/leads/contactStageHistory'
import React, { useMemo, useState } from 'react'
import { useContacts } from 'hooks/contacts/useContacts'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import { useLeads } from 'hooks/admin/useLeads'
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads'
import { useUsers } from 'hooks/useUsers'
import {
  ActivityTimelineEntryDate,
  ActivityTimelineEntryTitle,
} from 'views/SuperAdmin/Management/LeadManagement/components/ActivityTimelineEntry'
import { ActivityDomainLabel } from 'views/SuperAdmin/Management/LeadManagement/components/ActivityDomainLabel'
import {
  ContactActivityLogDialog,
  type LoggableActivityType,
} from 'views/SuperAdmin/Management/LeadManagement/components/dialogs'
import { activityAuthorDisplayName } from 'views/SuperAdmin/Management/LeadManagement/util/activityDisplay'
import { activityDomainPaperSx } from 'views/SuperAdmin/Management/LeadManagement/util/activityDomainPaperSx'
import { normalizeText } from 'views/SuperAdmin/Management/LeadManagement/util/customerUtils'
import { openContactDetailInNewTab } from 'views/SuperAdmin/Management/LeadManagement/util/contactRoutes'
import {
  buildContactMergedTimelineEntries,
  contactMergedTimelineEntryAuthorId,
  filterContactMergedTimelineEntries,
  getContactMergedTimelineDisplay,
  getContactMergedTimelineDomain,
  type ContactMergedActivityFilter,
  type ContactMergedTimelineEntry,
  type CrmActivityDomainFilter,
} from 'views/SuperAdmin/Management/LeadManagement/util/contactMergedTimeline'

interface TimelineCustomerTabProps {
  vehicle: Vehicle
}

interface ActivityIconMeta {
  icon: React.ReactNode
  color: (palette: Theme['palette']) => string
}

const ACTIVITY_ICON_META: Record<ContactActivityType | 'created', ActivityIconMeta> = {
  created: {
    icon: <PersonAddAlt1Icon fontSize="small" />,
    color: (p) => p.primary.main,
  },
  call: {
    icon: <PhoneIcon fontSize="small" />,
    color: (p) => p.success.main,
  },
  sms: {
    icon: <SmsIcon fontSize="small" />,
    color: (p) => p.info.main,
  },
  email: {
    icon: <EmailIcon fontSize="small" />,
    color: (p) => p.primary.main,
  },
  note: {
    icon: <NoteIcon fontSize="small" />,
    color: (p) => p.warning.main,
  },
  task: {
    icon: <TaskIcon fontSize="small" />,
    color: (p) => p.secondary.main,
  },
  lead: {
    icon: <PostAddIcon fontSize="small" />,
    color: (p) => p.primary.dark,
  },
  shipping_repairs: {
    icon: <LocalShippingIcon fontSize="small" />,
    color: (p) => p.secondary.main,
  },
}

type ActivityFilter = ContactMergedActivityFilter

const ACTIVITY_FILTER_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'All activity' },
  { value: 'created', label: 'Contact created' },
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Emails' },
  { value: 'note', label: 'Notes' },
  { value: 'task', label: 'Tasks' },
  { value: 'lead', label: 'Leads' },
  { value: 'shipping_repairs', label: 'Shipping & Repairs' },
]

const DOMAIN_FILTER_OPTIONS: { value: CrmActivityDomainFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sales', label: 'Sales' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'marketing', label: 'Marketing' },
]

const STAGE_LABEL: Record<ContactStage, string> = {
  prospect: 'Prospect',
  opportunity: 'Opportunity',
  customer: 'Customer',
  unqualified: 'Unqualified',
}

const stageAccentColor = (stage: ContactStage, palette: Theme['palette']): string => {
  if (stage === 'customer') return palette.success.main
  if (stage === 'opportunity') return palette.info.main
  if (stage === 'unqualified') return palette.text.secondary
  return palette.warning.main
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

const formatDate = (date: Date | null | undefined): string => {
  if (!date) return '—'
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return '—'
  return dateFormatter.format(d)
}

function findContactForVehicle(
  contacts: Contact[] | undefined,
  vehicle: Vehicle,
): Contact | null {
  if (!contacts?.length) return null
  const candidates = [vehicle.sold_to_user, vehicle.selling_to_user]
  for (const buyer of candidates) {
    if (!buyer) continue
    if (buyer.id) {
      const byUserId = contacts.find((c) => c.userId && c.userId === buyer.id)
      if (byUserId) return byUserId
    }
    const nameKey = normalizeText(buyer.name)
    if (nameKey) {
      const byName = contacts.find((c) => normalizeText(c.name) === nameKey)
      if (byName) return byName
    }
  }
  return null
}

/**
 * Rebuilt ContactPreviewPane "Recent activities" section for the vehicle timeline
 * Customer tab (not a shared extract of that pane).
 */
const TimelineCustomerTab: React.FC<TimelineCustomerTabProps> = ({ vehicle }) => {
  const theme = useTheme()
  const { contacts, logContactActivity, isLoggingActivity } = useContacts()
  const { companies: contactCompanies } = useContactCompanies()
  const { leads } = useLeads()
  const { inventoryLeads } = useInventoryLeads()
  const { users } = useUsers()

  const contact = useMemo(
    () => findContactForVehicle(contacts, vehicle),
    [contacts, vehicle],
  )

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [logType, setLogType] = useState<LoggableActivityType | null>(null)
  const [activitySearch, setActivitySearch] = useState('')
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all')
  const [activityDomainFilter, setActivityDomainFilter] =
    useState<CrmActivityDomainFilter>('all')

  const memberCompanies = useMemo(
    () =>
      contact
        ? contactCompanies.filter((company) =>
            company.members.some((m) => m.contactId === contact.id),
          )
        : [],
    [contactCompanies, contact],
  )

  const timelineEntries = useMemo<ContactMergedTimelineEntry[]>(() => {
    if (!contact) return []
    return buildContactMergedTimelineEntries({
      contact,
      memberCompanies,
      leads: leads ?? [],
      inventoryLeads: inventoryLeads ?? [],
    })
  }, [contact, memberCompanies, leads, inventoryLeads])

  const visibleTimelineEntries = useMemo<ContactMergedTimelineEntry[]>(
    () =>
      filterContactMergedTimelineEntries(
        timelineEntries,
        activitySearch,
        activityFilter,
        activityDomainFilter,
      ),
    [timelineEntries, activitySearch, activityFilter, activityDomainFilter],
  )

  const openMenu = (event: React.MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)
  const closeMenu = () => setMenuAnchor(null)

  const handlePickLog = (type: LoggableActivityType) => {
    closeMenu()
    setLogType(type)
  }

  const handleLogSubmit = async (note: string, domain: CrmActivityDomain) => {
    if (!contact || !logType) return
    await logContactActivity({ contactId: contact.id, type: logType, note, domain })
    setLogType(null)
  }

  if (!contact) {
    const buyerName =
      vehicle.sold_to_user?.name?.trim() ||
      vehicle.selling_to_user?.name?.trim() ||
      null
    return (
      <Box
        sx={{
          py: 4,
          px: 2,
          textAlign: 'center',
          color: 'text.secondary',
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" sx={{ mb: buyerName ? 0.5 : 0 }}>
          No CRM contact is linked to this vehicle.
        </Typography>
        {buyerName ? (
          <Typography variant="caption" color="text.secondary">
            Buyer on record: {buyerName}
          </Typography>
        ) : null}
      </Box>
    )
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography fontWeight={700}>Recent activities</Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => openContactDetailInNewTab(contact.id)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontWeight: 600,
          }}
        >
          {contact.name || 'Contact'}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="stretch" sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          value={activitySearch}
          onChange={(e) => setActivitySearch(e.target.value)}
          placeholder="Search"
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': { height: '100%' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, bgcolor: 'background.paper' },
          }}
        />
        <TextField
          select
          size="small"
          value={activityDomainFilter}
          onChange={(e) =>
            setActivityDomainFilter(e.target.value as CrmActivityDomainFilter)
          }
          sx={{
            flexShrink: 0,
            minWidth: 100,
            '& .MuiOutlinedInput-root': { height: '100%' },
            '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
          }}
          InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
        >
          {DOMAIN_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={activityFilter}
          onChange={(e) => setActivityFilter(e.target.value as ActivityFilter)}
          sx={{
            flexShrink: 0,
            minWidth: 110,
            '& .MuiOutlinedInput-root': { height: '100%' },
            '& .MuiSelect-select': { py: 1, display: 'flex', alignItems: 'center' },
          }}
          InputProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}
        >
          {ACTIVITY_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={openMenu}
          sx={{
            flexShrink: 0,
            px: 1.5,
            fontSize: 14,
            lineHeight: 1.25,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          Add
        </Button>
      </Stack>

      <Box
        sx={{
          maxHeight: '50vh',
          overflowY: 'auto',
          pr: 0.25,
          scrollbarGutter: 'stable',
        }}
      >
        {visibleTimelineEntries.length === 0 ? (
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
              color: 'text.secondary',
              border: 1,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2">No activities match your filters.</Typography>
          </Box>
        ) : (
          <Timeline
            sx={{
              m: 0,
              p: 0,
              '& .MuiTimelineItem-root:before': { display: 'none', flex: 0, padding: 0 },
            }}
          >
            {visibleTimelineEntries.map((entry, index) => {
              const isLast = index === visibleTimelineEntries.length - 1
              const display = getContactMergedTimelineDisplay(entry)
              const meta = ACTIVITY_ICON_META[display.activityType]
              const iconColor = meta.color(theme.palette)
              const authorName = activityAuthorDisplayName(
                users,
                contactMergedTimelineEntryAuthorId(entry),
              )
              const entryStage = getContactStageAt(
                contact.stageHistory,
                entry.createdAt,
                contact.stage,
              )
              const entryStageAccent = stageAccentColor(entryStage, theme.palette)
              const domain = getContactMergedTimelineDomain(entry)
              return (
                <TimelineItem key={entry.id}>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        bgcolor: alpha(iconColor, 0.16),
                        color: iconColor,
                        boxShadow: 'none',
                        m: 0,
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {meta.icon}
                    </TimelineDot>
                    {!isLast && <TimelineConnector sx={{ bgcolor: 'divider' }} />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pb: 1.5, pr: 0 }}>
                    <Paper
                      variant="outlined"
                      sx={activityDomainPaperSx(domain)}
                      title={
                        domain === 'inventory'
                          ? 'Inventory activity'
                          : domain === 'sales'
                            ? 'Sales activity'
                            : undefined
                      }
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ rowGap: 0.5 }}
                        >
                          <ActivityTimelineEntryTitle
                            title={display.title}
                            authorName={authorName}
                            variant="body2"
                          />
                          {display.companyName ? (
                            <Chip
                              size="small"
                              icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                              label={display.companyName}
                              sx={{
                                height: 18,
                                fontSize: 10.5,
                                fontWeight: 600,
                                bgcolor: alpha(theme.palette.secondary.main, 0.12),
                                color: theme.palette.secondary.dark,
                                '& .MuiChip-icon': {
                                  color: 'inherit',
                                  ml: 0.5,
                                },
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            label={STAGE_LABEL[entryStage]}
                            sx={{
                              height: 18,
                              fontSize: 10.5,
                              fontWeight: 600,
                              bgcolor: alpha(entryStageAccent, 0.14),
                              color: entryStageAccent,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </Stack>
                        <ActivityTimelineEntryDate dateLabel={formatDate(entry.createdAt)} />
                      </Box>
                      {display.body && (
                        <>
                          <Box sx={{ px: 1.5 }}>
                            <Divider />
                          </Box>
                          <Box sx={{ p: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {display.body}
                            </Typography>
                          </Box>
                        </>
                      )}
                      <ActivityDomainLabel domain={domain} />
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              )
            })}
          </Timeline>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              mt: 0.75,
              minWidth: 240,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            },
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            Add activity
          </Typography>
        </Box>
        {(
          [
            {
              type: 'call' as const,
              label: 'Log a call',
              subtitle: 'Phone call summary',
              icon: <PhoneIcon fontSize="small" />,
              accent: theme.palette.success.main,
            },
            {
              type: 'sms' as const,
              label: 'Log SMS',
              subtitle: 'Text message exchange',
              icon: <SmsIcon fontSize="small" />,
              accent: theme.palette.info.main,
            },
            {
              type: 'email' as const,
              label: 'Log an email',
              subtitle: 'Email correspondence',
              icon: <EmailIcon fontSize="small" />,
              accent: theme.palette.primary.main,
            },
            {
              type: 'note' as const,
              label: 'Create a note',
              subtitle: 'Internal note',
              icon: <NoteIcon fontSize="small" />,
              accent: theme.palette.warning.main,
            },
            {
              type: 'shipping_repairs' as const,
              label: 'Shipping & Repairs',
              subtitle: 'Shipping or repair update',
              icon: <LocalShippingIcon fontSize="small" />,
              accent: theme.palette.secondary.main,
            },
          ] as const
        ).map((item) => (
          <MenuItem
            key={item.type}
            onClick={() => handlePickLog(item.type)}
            sx={{
              py: 1,
              px: 1.75,
              '&:hover': { bgcolor: alpha(item.accent, 0.08) },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
                color: item.accent,
                '& .MuiSvgIcon-root': { color: item.accent },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: alpha(item.accent, 0.14),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.subtitle}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
      </Menu>

      <ContactActivityLogDialog
        open={Boolean(logType)}
        type={logType}
        contactName={contact.name}
        isSubmitting={isLoggingActivity}
        onClose={() => setLogType(null)}
        onSubmit={handleLogSubmit}
      />
    </Box>
  )
}

export default TimelineCustomerTab
